import { makeAutoObservable, runInAction } from 'mobx'
import type { Notify } from '@/Ship/Contracts/Notify'
import { DeleteExecutorAssignmentTask } from '../Tasks/DeleteExecutorAssignmentTask'
import { UpdateExecutorAssignmentTask } from '../Tasks/UpdateExecutorAssignmentTask'

/**
 * Local store of the edit-mode vehicle-needs card (`VehicleNeeds` → `ExecutorBlock`).
 *
 * Owns the edit-mode UI state for the vehicle-needs editor:
 *   - per-assignment pending field overrides (mass save on form submit);
 *   - locally appended / removed assignments per executor (the request DTO
 *     isn't reloaded after every add/delete — we mirror changes optimistically);
 *   - in-flight delete-task flag.
 *
 * Async methods talk to backend through `Tasks/` wrappers and surface user feedback
 * via the injected `Notify` callbacks.
 *
 * Form-level integration is done through `subscribeFormEvents(em)`, which:
 *   - strips `vehicleNeeds` from the outgoing save payload (root form doesn't own this field);
 *   - flushes `pendingChanges` via parallel PATCH requests just before the form save.
 *
 * Instantiated per drawer mount via `useState(() => new VehicleNeedsEditStore())`.
 */
export class VehicleNeedsEditStore {
    pendingChanges: Record<string, Record<string, any>> = {}
    localAssignmentsByExecutor: Record<string, any[]> = {}
    deletingTaskUuid: string | null = null

    constructor() {
        makeAutoObservable(this)
    }

    /**
     * Re-seed `localAssignmentsByExecutor` from the freshest `vehicleNeeds` props.
     * Called by the view on mount and whenever `vehicleNeeds` changes.
     */
    syncFromVehicleNeeds(vehicleNeeds: any[]): void {
        const map: Record<string, any[]> = {}
        ;(vehicleNeeds || []).forEach((vn: any) => {
            ;(vn.executors || []).forEach((ex: any) => {
                if (ex.uuid) map[ex.uuid] = ex.assignments || []
            })
        })
        this.localAssignmentsByExecutor = map
    }

    setFieldChange(uuid: string, field: string, value: any): void {
        this.pendingChanges = {
            ...this.pendingChanges,
            [uuid]: { ...(this.pendingChanges[uuid] ?? {}), [field]: value },
        }
    }

    /**
     * Append a freshly-created assignment into the local map so the table refreshes
     * without waiting for a full form reload. Called by `AddTaskModalStore.submit`.
     */
    appendAssignment(executorUuid: string, assignment: any): void {
        this.localAssignmentsByExecutor = {
            ...this.localAssignmentsByExecutor,
            [executorUuid]: [...(this.localAssignmentsByExecutor[executorUuid] || []), assignment],
        }
    }

    async deleteTask(taskUuid: string, executorUuid: string, notify: Notify): Promise<void> {
        this.deletingTaskUuid = taskUuid
        try {
            await DeleteExecutorAssignmentTask(taskUuid)
            runInAction(() => {
                this.localAssignmentsByExecutor = {
                    ...this.localAssignmentsByExecutor,
                    [executorUuid]: (this.localAssignmentsByExecutor[executorUuid] || []).filter(
                        (a: any) => a.uuid !== taskUuid
                    ),
                }
                const nextChanges = { ...this.pendingChanges }
                delete nextChanges[taskUuid]
                this.pendingChanges = nextChanges
            })
            notify.success('Завдання видалено')
        } catch (e: any) {
            notify.error(e?.message || 'Не вдалося видалити завдання')
        } finally {
            runInAction(() => {
                this.deletingTaskUuid = null
            })
        }
    }

    /**
     * Subscribe to the form's EventManager so that:
     *   - `vehicleNeeds` is dropped from the outgoing save payload;
     *   - pending assignment changes are flushed via UpdateExecutorAssignmentTask
     *     just before the request entity itself is saved.
     *
     * Returns a cleanup function the view must call on unmount.
     */
    subscribeFormEvents(eventManager: any, notify: Notify): () => void {
        if (!eventManager) return () => {}

        eventManager.on(
            'onBeforeTransformSendSaveRequest',
            'stripVehicleNeeds',
            ({ values }: { values: Record<string, any> }) => {
                delete values['vehicleNeeds']
            }
        )

        eventManager.on('onBeforeSendSaveRequest', 'saveAssignmentChanges', async (): Promise<false | void> => {
            const entries = Object.entries(this.pendingChanges)
            if (entries.length === 0) return
            try {
                await Promise.all(entries.map(([uuid, fields]) => UpdateExecutorAssignmentTask(uuid, fields)))
                runInAction(() => {
                    this.pendingChanges = {}
                })
            } catch {
                notify.error('Помилка збереження призначень')
                return false
            }
        })

        return () => {
            eventManager.off('onBeforeTransformSendSaveRequest', 'stripVehicleNeeds')
            eventManager.off('onBeforeSendSaveRequest', 'saveAssignmentChanges')
        }
    }
}
